/**
 * components/branding/BrandMonogram.tsx
 *
 * Fallback avatar shown when the sender has no logo uploaded.
 * Renders initials on a neutral background; falls back to a generic
 * User icon when no initials are available either.
 *
 * Created Apr 2026: part of the continuous branding refactor for the
 *   client-facing form experience.
 */

import { User } from 'lucide-react';

interface BrandMonogramProps {
  initials: string | null;
  size: 'sm' | 'md' | 'lg';
}

const SIZE_CLASSES: Record<BrandMonogramProps['size'], { outer: string; text: string; icon: string }> = {
  sm: { outer: 'w-8 h-8',  text: 'text-xs font-semibold',  icon: 'w-4 h-4' },
  md: { outer: 'w-12 h-12', text: 'text-sm font-semibold', icon: 'w-5 h-5' },
  lg: { outer: 'w-20 h-20', text: 'text-xl font-semibold', icon: 'w-8 h-8' },
};

export default function BrandMonogram({ initials, size }: BrandMonogramProps) {
  const cls = SIZE_CLASSES[size];
  return (
    <div
      className={`${cls.outer} rounded-full bg-neutral-100 flex items-center justify-center flex-shrink-0`}
      aria-hidden="true"
    >
      {initials ? (
        <span className={`${cls.text} text-neutral-700`}>{initials}</span>
      ) : (
        <User className={`${cls.icon} text-neutral-400`} />
      )}
    </div>
  );
}
